import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Play } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const words = ["Plan", "Shoot", "Edit", "Promote", "Design", "Develop", "Deploy"];

const isTouchDevice = () =>
  typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches;

const HeroSection = () => {
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 200]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  const isTouch = useRef(isTouchDevice());
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (isTouch.current) return;
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) => (prev + 1) % words.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50, filter: "blur(10px)" },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
      {/* Interactive Spotlight */}
      <motion.div
        className="pointer-events-none fixed inset-0 z-30 transition-opacity duration-300"
        animate={{
          background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(99, 102, 241, 0.08), transparent 80%)`,
        }}
      />

      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] -z-20" />

      {/* Animated Orbs — static on touch devices to avoid GPU strain */}
      <div className="absolute top-0 left-0 w-full h-full -z-10">
        <motion.div
          animate={isTouch.current ? {} : { scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/20 blur-[120px] rounded-full"
        />
        <motion.div
          animate={isTouch.current ? {} : { scale: [1, 1.5, 1], rotate: [0, -90, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-secondary/15 blur-[120px] rounded-full"
        />
        <motion.div
          animate={isTouch.current ? {} : { x: [0, 100, 0], y: [0, -100, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-[40%] right-[20%] w-[30%] h-[30%] bg-accent/20 blur-[100px] rounded-full"
        />
      </div>

      <div className="container text-center relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          style={{ y: y1, opacity }}
          className="flex flex-col items-center"
        >
          <motion.span
            variants={itemVariants}
            className="inline-block px-5 py-2 rounded-full bg-surface/80 backdrop-blur-md border border-white/10 text-primary text-xs font-bold uppercase tracking-[0.2em] mb-8 shadow-xl shadow-primary/10"
          >
            Media Reinvented
          </motion.span>

          <h1 className="text-5xl md:text-8xl lg:text-9xl font-black mb-6 tracking-tighter leading-[1.2] flex flex-col items-center gap-2">
            <div className="flex items-center gap-6 overflow-visible">
              <motion.span variants={itemVariants}>We</motion.span>
              <div className="relative inline-block min-w-[2.5em] text-center">
                <AnimatePresence mode="wait">
                  <motion.span
                    key={words[index]}
                    initial={{ y: 40, opacity: 0, filter: "blur(10px)" }}
                    animate={{ y: 0, opacity: 1, filter: "blur(0px)" }}
                    exit={{ y: -40, opacity: 0, filter: "blur(10px)" }}
                    transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute -inset-y-2 left-0 right-0 py-2 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent"
                  >
                    {words[index]}
                  </motion.span>
                </AnimatePresence>
                {/* Invisible text to maintain layout width */}
                <span className="opacity-0 pointer-events-none py-2">Promote</span>
              </div>
            </div>
            <motion.div 
              variants={itemVariants} 
              className="text-2xl md:text-4xl lg:text-5xl mt-8 text-white/70 font-bold tracking-tight"
            >
              Your All-in-one Media Partner.
            </motion.div>
          </h1>

          <motion.p
            variants={itemVariants}
            className="max-w-2xl mx-auto text-text-muted text-lg md:text-xl mb-12 font-medium"
          >
            From scripting to final video, social media management, and web development.
            We turn your vision into viral reality.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
          >
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="#work"
              className="btn-primary group relative overflow-hidden flex items-center gap-2 px-8 py-4 text-lg"
            >
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
              <Play size={20} fill="white" className="relative z-10" />
              <span className="relative z-10">View Our Work</span>
            </motion.a>
            <motion.a
              whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.1)" }}
              whileTap={{ scale: 0.95 }}
              href="#contact"
              className="px-8 py-4 rounded-xl border border-white/20 bg-white/5 backdrop-blur-sm transition-all text-white font-semibold text-lg"
            >
              Get in Touch
            </motion.a>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-text-muted font-bold">Scroll</span>
        <motion.div
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="w-[1px] h-16 bg-gradient-to-b from-primary via-primary/50 to-transparent"
        />
      </motion.div>
    </section>
  );
};

export default HeroSection;
